import React, { useMemo, useState } from "react";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../../component/buttonComponent/ButtonComponent";
import CreateInstitutionModal from "../../../component/modal/CreateInstitutionModal";
import EditInstitutionModal from "../../../component/modal/EditInstitutionModal";
import SnackBar from "../../../component/Snackbar/Snackbar";
import SearchInput from "../../../component/searchInput/SearchInput";
import LoadingSpinner from "../../../component/loading/LoadingSpinner";
import { useMasterAdmin } from "../MasterAdminContext";
import "./MasterAdminInstitutions.css";

const formatTripTypeLabel = (tripType) => {
  if (typeof tripType === "string") return tripType;
  if (tripType && typeof tripType === "object") {
    return tripType.name || tripType.label || tripType.id || String(tripType);
  }
  return String(tripType);
};

const MasterAdminInstitutions = () => {
  const { institutions, loading, fetchInstitutions } = useMasterAdmin();
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [modalLoading, setModalLoading] = useState(false);

  const filteredInstitutions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return institutions;
    return institutions.filter(
      (inst) =>
        inst.institutionName?.toLowerCase().includes(query) ||
        inst.institutionId?.toLowerCase().includes(query)
    );
  }, [institutions, search]);

  const handleOpenEdit = (institution) => {
    setEditingInstitution(institution);
    setShowEditModal(true);
  };

  const handleSaveEditedInstitution = () => {
    fetchInstitutions();
    setSnackbarType("success");
    setSnackbarTitle("Institution updated");
    setSnackbarSubtitle("Changes have been saved.");
    setShowSnackbar(true);
  };

  const handleInstitutionDeleted = () => {
    fetchInstitutions();
    setShowEditModal(false);
    setEditingInstitution(null);
    setSnackbarType("success");
    setSnackbarTitle("Institution deleted");
    setSnackbarSubtitle("The institution has been removed.");
    setShowSnackbar(true);
  };

  const showError = (title, message) => {
    setSnackbarType("error");
    setSnackbarTitle(title);
    setSnackbarSubtitle(message);
    setShowSnackbar(true);
  };

  return (
    <div className="ma-institutions">
      <div className="ma-institutions-toolbar">
        <div className="ma-institutions-toolbar-left">
          <SearchInput
            containerWidth="22rem"
            containerHeight="2.5rem"
            text="Search by name or ID..."
            value={search}
            onChangeText={(e) => setSearch(e.target.value)}
          />
          <span className="ma-institutions-count">
            {filteredInstitutions.length} institution
            {filteredInstitutions.length === 1 ? "" : "s"}
          </span>
        </div>
        <PrimaryButton
          label="Create Institution"
          onClickFunction={() => setShowCreateModal(true)}
        />
      </div>

      {loading && institutions.length === 0 ? (
        <div className="ma-institutions-loading">
          <LoadingSpinner size="large" color="#ffb502" />
        </div>
      ) : filteredInstitutions.length === 0 ? (
        <div className="ma-institutions-empty">
          <h3>No institutions found</h3>
          <p>
            {search
              ? "Try a different search term or clear the filter."
              : "Create your first institution workspace to get started."}
          </p>
          {!search && (
            <PrimaryButton
              label="Create Institution"
              onClickFunction={() => setShowCreateModal(true)}
            />
          )}
        </div>
      ) : (
        <div className="ma-institutions-grid">
          {filteredInstitutions.map((institution) => {
            const tripTypes = Array.isArray(institution.tripTypes)
              ? institution.tripTypes
              : [];
            const totals = institution.totals ?? {};

            return (
              <article
                key={institution.institutionId}
                className="ma-institution-card"
              >
                <div className="ma-institution-card-header">
                  <div className="ma-institution-card-title-wrap">
                    <h3 className="ma-institution-card-title">
                      {institution.institutionName}
                    </h3>
                    <p className="ma-institution-card-id">
                      {institution.institutionId}
                    </p>
                  </div>
                  <SecondaryButton
                    width="4.25rem"
                    label="Edit"
                    onClickFunction={() => handleOpenEdit(institution)}
                  />
                </div>

                <div className="ma-institution-card-meta">
                  <span className="ma-institution-meta-label">Timezone</span>
                  <span className="ma-institution-meta-value">
                    {(institution.timezone ?? "Asia/Kuala_Lumpur").replace(
                      /_/g,
                      " "
                    )}
                  </span>
                </div>

                <div className="ma-institution-card-tags">
                  {tripTypes.length > 0 ? (
                    tripTypes.map((tripType, index) => (
                      <span
                        key={`${formatTripTypeLabel(tripType)}-${index}`}
                        className="ma-institution-tag"
                      >
                        {formatTripTypeLabel(tripType)}
                      </span>
                    ))
                  ) : (
                    <span className="ma-institution-tag ma-institution-tag--muted">
                      No trip types
                    </span>
                  )}
                </div>

                <div className="ma-institution-card-stats">
                  {[
                    { key: "admins", label: "Admins" },
                    { key: "parents", label: "Parents" },
                    { key: "children", label: "Children" },
                    { key: "drivers", label: "Drivers" },
                    { key: "vehicles", label: "Vehicles" },
                  ].map(({ key, label }) => (
                    <div key={key} className="ma-institution-stat">
                      <span className="ma-institution-stat-value">
                        {totals[key] ?? "—"}
                      </span>
                      <span className="ma-institution-stat-label">{label}</span>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <CreateInstitutionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        setShowSnackbar={setShowSnackbar}
        setSnackbarSubtitle={setSnackbarSubtitle}
        setSnackbarTitle={setSnackbarTitle}
        setSnackbarType={setSnackbarType}
        loading={modalLoading}
        setLoading={setModalLoading}
        fetchInstitutions={fetchInstitutions}
      />

      <EditInstitutionModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingInstitution(null);
        }}
        institution={editingInstitution}
        onSave={handleSaveEditedInstitution}
        onDeleted={handleInstitutionDeleted}
        onDeleteError={(message) => showError("Delete failed", message)}
        onPatchError={(message) => showError("Update failed", message)}
        fetchInstitutions={fetchInstitutions}
      />

      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
};

export default MasterAdminInstitutions;
